<script setup>
import { computed } from 'vue';

const props = defineProps({
  media: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'view']);

// Format date
const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const categoryIcon = computed(() => {
  const icons = {
    usp: 'i-lucide-star',
    benefits: 'i-lucide-heart',
    pain_points: 'i-lucide-alert-circle',
    features: 'i-lucide-zap',
    how_to_use: 'i-lucide-play-circle',
    ingredients: 'i-lucide-flask-conical',
  };
  return icons[props.media.category] || 'i-lucide-folder';
});

const handleEdit = () => {
  emit('edit', props.media);
};

const handleDelete = () => {
  emit('delete', props.media);
};

const handleView = () => {
  emit('view', props.media);
};
</script>

<template>
  <div
    class="group relative bg-white dark:bg-n-slate-2 border border-n-slate-3 rounded-xl overflow-hidden hover:border-woot-500 hover:shadow-lg transition-all cursor-pointer"
    @click="handleView"
  >
    <!-- Thumbnail -->
    <div class="relative aspect-video bg-n-slate-2 overflow-hidden">
      <img
        v-if="media.thumbnailUrl"
        :src="media.thumbnailUrl"
        :alt="media.title"
        class="w-full h-full object-cover"
      />
      <div
        v-else
        class="w-full h-full flex items-center justify-center bg-gradient-to-br from-n-slate-3 to-n-slate-4"
      >
        <span class="i-lucide-image w-12 h-12 text-n-slate-6" />
      </div>

      <!-- Category Badge -->
      <div
        class="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white text-xs font-medium"
      >
        <span :class="categoryIcon" class="w-3.5 h-3.5" />
        {{ media.category || 'Media' }}
      </div>

      <!-- Actions Overlay -->
      <div
        class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2"
      >
        <button
          class="p-2 bg-white/90 hover:bg-white rounded-full text-n-slate-12 transition-colors"
          @click.stop="handleEdit"
        >
          <span class="i-lucide-edit-2 w-4 h-4" />
        </button>
        <button
          class="p-2 bg-white/90 hover:bg-white rounded-full text-ruby-9 transition-colors"
          @click.stop="handleDelete"
        >
          <span class="i-lucide-trash-2 w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="p-4">
      <h3 class="text-sm font-semibold text-n-slate-12 line-clamp-1 mb-1">
        {{ media.title }}
      </h3>
      <p class="text-xs text-n-slate-10 line-clamp-2 mb-3">
        {{ media.description }}
      </p>

      <!-- Meta -->
      <div class="flex flex-col gap-1 text-xs text-n-slate-9">
        <div v-if="media.createdAt" class="flex items-center gap-1">
          <span>Created: {{ formatDate(media.createdAt) }}</span>
        </div>
        <div v-if="media.updatedAt" class="flex items-center gap-1">
          <span>Updated: {{ formatDate(media.updatedAt) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
